import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("checking");
  const [detail, setDetail] = useState(null);
  const polls = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setDetail(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (err) {
        console.error("Payment status check failed, will retry:", err);
      }
      polls.current += 1;
      if (polls.current > 12) {
        setStatus("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div className="pt-32 min-h-[70vh] flex items-center justify-center" data-testid="payment-success-page">
      <div className="max-w-xl mx-auto px-6 text-center">
        {status === "checking" && <p className="font-serif text-3xl text-matte/50">Confirming your payment…</p>}
        {status === "paid" && (
          <div data-testid="order-confirmed">
            <CheckCircle2 size={56} strokeWidth={1} className="text-sage mx-auto mb-8" />
            <p className="text-[12px] uppercase tracking-editorial text-plum mb-4">
              {detail?.type === "enrollment" ? "Enrollment Confirmed" : "Order Confirmed"}
            </p>
            <h1 className="font-serif text-5xl font-light mb-6">Thank you.</h1>
            <p className="text-matte/60 leading-relaxed mb-4">
              {detail?.type === "enrollment"
                ? "Your child's place is reserved. We can't wait to welcome your family to the studio."
                : "Your order has been received. A confirmation is available below."}
            </p>
            {detail?.order_id && (
              <p className="text-[11px] uppercase tracking-wide-sm text-matte/40 mb-8" data-testid="order-id">
                Order · {detail.order_id.slice(0, 8)}
              </p>
            )}
            <Link to="/" className="inline-block border border-matte px-9 py-4 text-[12px] uppercase tracking-editorial hover:bg-matte hover:text-ivory transition-colors duration-300">
              Return Home
            </Link>
          </div>
        )}
        {(status === "failed" || status === "timeout" || status === "error") && (
          <div data-testid="payment-issue">
            <h1 className="font-serif text-4xl font-light mb-6">We couldn't confirm your payment.</h1>
            <p className="text-matte/60 mb-8">If you were charged, please contact us and we'll resolve it right away.</p>
            <Link to="/cart" className="border border-matte px-8 py-4 text-[12px] uppercase tracking-editorial hover:bg-matte hover:text-ivory transition-colors">Back to Cart</Link>
          </div>
        )}
      </div>
    </div>
  );
}
